import { makeAutoObservable } from 'mobx';
import { store as NotificationsStore } from 'react-notifications-component';
import AppNotifications from 'Constants/AppNotifications/AppNotifications'
import NotificationsTypes from 'Constants/NotificationsTypes'
import STANDARD_DISMISS from 'Constants/AppNotifications/StandardDismiss'
import i18n from 'i18n'

class uiStore {
  appNotifications = AppNotifications

  isSidebarVisible = (window.innerWidth > 992)

  /** @type {null | {
   *  open: boolean,
   *  title?: string,
   *  message: string,
   *  confirmLabel?: string,
   *  cancelLabel?: string,
   *  variant?: string,
   *  confirmButtonVariant?: string,
   *  showCancel?: boolean,
   *  resolve?: (value: boolean) => void
   * }} */
  dialog = null

  constructor() {
    makeAutoObservable(this)
  }

  toggleSidebar() {
    this.isSidebarVisible = !this.isSidebarVisible
    this.lockBodyScroll()
  }

  closeSidebar(){
    this.isSidebarVisible = false;
    this.lockBodyScroll()
  }

  lockBodyScroll(){
    if (!(window.innerWidth > 992)) {
      window.document.body.style.overflow = this.isSidebarVisible ? "hidden" : "auto"
    }
  }
  
  addNotification(notification) {
    NotificationsStore.addNotification({
      insert: "top",
      container: "bottom-right",
      animationIn: ["animated", "fadeIn"],
      animationOut: ["animated", "fadeOut"],
      dismiss: STANDARD_DISMISS,
      ...notification,
      message: i18n.t('notifications:'+notification.message),
    })
  }

  /**
   * Toast with an already-translated (or plain) message.
   * Prefer this over window.alert for success / info / warning / error feedback.
   */
  notify({ type = NotificationsTypes.INFO, message, title, duration, dismiss } = {}) {
    if (!message) return

    NotificationsStore.addNotification({
      insert: 'top',
      container: 'bottom-right',
      animationIn: ['animated', 'fadeIn'],
      animationOut: ['animated', 'fadeOut'],
      type,
      title,
      message,
      dismiss: dismiss || {
        ...STANDARD_DISMISS,
        ...(duration != null ? { duration } : {}),
      },
    })
  }

  notifySuccess(message, options = {}) {
    this.notify({ type: NotificationsTypes.SUCCESS, message, ...options })
  }

  notifyError(message, options = {}) {
    this.notify({ type: NotificationsTypes.ERROR, message, ...options })
  }

  notifyWarning(message, options = {}) {
    this.notify({ type: NotificationsTypes.WARNING, message, ...options })
  }

  notifyInfo(message, options = {}) {
    this.notify({ type: NotificationsTypes.INFO, message, ...options })
  }

  /**
   * Professional confirm dialog (replaces window.confirm).
   * @returns {Promise<boolean>}
   */
  confirm({
    message,
    title,
    confirmLabel,
    cancelLabel,
    variant = 'warning',
    confirmButtonVariant,
    showCancel = true,
  } = {}) {
    if (this.dialog?.resolve) {
      this.dialog.resolve(false)
    }

    return new Promise((resolve) => {
      this.dialog = {
        open: true,
        message: message || '',
        title,
        confirmLabel,
        cancelLabel,
        variant,
        confirmButtonVariant,
        showCancel,
        resolve,
      }
    })
  }

  /**
   * Acknowledgement dialog (replaces window.alert when a modal is preferred).
   * @returns {Promise<boolean>}
   */
  alertDialog({
    message,
    title,
    confirmLabel,
    variant = 'info',
    confirmButtonVariant,
  } = {}) {
    return this.confirm({
      message,
      title,
      confirmLabel,
      variant,
      confirmButtonVariant,
      showCancel: false,
    })
  }

  resolveDialog(result) {
    const current = this.dialog
    this.dialog = null
    if (current?.resolve) {
      current.resolve(Boolean(result))
    }
  }
}

export default new uiStore();
